import React, { useMemo } from 'react'
import { CustomDropdownItemOption, DropDownItemOptionComponentProps, SelectDropdown, SelectOption } from '../../components/SelectDropdown/SelectDropdown'
import { ScopedBlock } from '../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../components/ScopedBlock/models'
import { IntegrationIcon } from '../../components/IntegrationIcon'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { useGetAccountQuery } from '../../store/accountUserSettings/accountUserSettings'
import { getIntegrationDisplayName } from '../../common/integrations'
import { getMappedIntegrationObjects } from './utils'
import { useUpdateIntegrationObject } from './useUpdateIntegrationObject'
import { UpdateIntegrationObjectWizard } from './UpdateIntegrationObjectWizard'
import { UpdateIntegrationObjectModal } from './UpdateIntegrationObjectModal'
import './UpdateIntegrationObjectButton.scss'

export const UpdateIntegrationObjectButton: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const { data: account } = useGetAccountQuery({ accountId })
  const updateIntegrationObject = useUpdateIntegrationObject()

  const mappedObjects = useMemo(() => getMappedIntegrationObjects(account?.data), [account])

  const options = useMemo<SelectOption[]>(
    () =>
      mappedObjects.map((object) => {
        const integrationName = getIntegrationDisplayName(object.serviceId)
        return {
          id: `${object.serviceId}|${object.objectName}`,
          label: `Update ${object.displayName} to ${integrationName}`,
          displayLabel: (
            <div className='d-flex align-items-center gap-2'>
              <IntegrationIcon serviceId={object.serviceId} className='crm-icon' fill='var(--zocks-gray-900)' />
              <div>
                Update {object.displayName} to {integrationName}
              </div>
            </div>
          ),
        }
      }),
    [mappedObjects],
  )

  if (mappedObjects.length === 0) {
    return null
  }

  const handleHide = () => updateIntegrationObject.reset()

  const handleSelect = (option: SelectOption | null) => {
    const object = mappedObjects.find((o) => `${o.serviceId}|${o.objectName}` === option?.id)
    if (object) {
      updateIntegrationObject.begin(object)
    }
  }

  return (
    <ScopedBlock scope={{ scope: 'integration:manage_crm', superScope: ['integration:admin'] }} missingScopeAction={MissingScopeAction.HIDE}>
      <UpdateIntegrationObjectWizard updateIntegrationObject={updateIntegrationObject} onHide={handleHide} />
      <UpdateIntegrationObjectModal updateIntegrationObject={updateIntegrationObject} onHide={handleHide} />
      <div className='header-dropdown-container update-integration-object-dropdown-container d-none d-sm-block'>
        <SelectDropdown
          className='zocks-py-0.375 zocks-px-0.75'
          options={options}
          onOptionSelected={handleSelect}
          DropDownItemOptionComponent={UpdateIntegrationObjectDropdownItemOptionComponent}
          icon='bi-arrow-clockwise'
          label='Update'
        />
      </div>
    </ScopedBlock>
  )
}

const UpdateIntegrationObjectDropdownItemOptionComponent: React.FC<DropDownItemOptionComponentProps> = ({
  optionId,
  displayLabel,
  onClick,
  className,
  disabled,
}) => (
  <CustomDropdownItemOption role='button' key={optionId} onClick={onClick} className={className} aria-disabled={disabled}>
    {displayLabel}
  </CustomDropdownItemOption>
)
